// ============================================
// COMPONENT: UserModal
// ============================================
// Archivo: src/components/modals/UserModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button } from '../common';

const UserModal = ({
  show,
  user = null,
  plants = [],
  companies = [],
  currentUser = null,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Técnico',
    plant_id: '',
    companies: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!user;
  const isGlobal = currentUser?.scope === 'global';

  // Cargar datos si es edición
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'Técnico',
        plant_id: user.plant_id || '',
        companies: user.companies || (user.company_id ? [user.company_id] : [])
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'Técnico',
        plant_id: '',
        companies: []
      });
    }
    setErrors({});
  }, [user, show]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!isEdit && !formData.password) {
      newErrors.password = 'La contraseña es obligatoria para nuevos usuarios';
    }

    if (formData.password) {
      const hasLetter = /[a-zA-Z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasMinLength = formData.password.length >= 8;

      if (!hasLetter || !hasNumber || !hasMinLength) {
        newErrors.password = 'La contraseña debe tener al menos 8 caracteres, letras y números';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        plant_id: formData.role === 'Jefe de Planta' && formData.plant_id
          ? parseInt(formData.plant_id, 10)
          : null,
        ...(isGlobal && formData.companies.length > 0 && {
          companies: formData.companies,
          // company_id = primera empresa asignada; el backend filtra por ella en el JWT
          company_id: formData.companies[0]
        })
      };

      if (formData.password) {
        submitData.password = formData.password;
      }

      await onSubmit(submitData, user?.id);
      onClose();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = currentUser?.role === 'Administrador' && currentUser?.scope === 'global';
  const roleOptions = [
    ...(isSuperAdmin ? [{ value: 'Administrador', label: 'Administrador' }] : []),
    { value: 'Gerente General', label: 'Gerente General' },
    { value: 'Gerente', label: 'Gerente' },
    { value: 'Jefe de Planta', label: 'Jefe de Planta' },
    { value: 'Técnico', label: 'Técnico' },
    { value: 'Auditor', label: 'Auditor' }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nombre Completo"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: Juan Pérez"
          required
          error={errors.name}
        />

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="usuario@prt.cl"
          required
          error={errors.email}
        />

        <Input
          label={isEdit ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="••••••••"
          required={!isEdit}
          error={errors.password}
        />

        {formData.password && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="font-semibold text-blue-900 mb-1">La contraseña debe:</p>
            <ul className="list-disc list-inside text-blue-800 space-y-1">
              <li className={/[a-zA-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                Contener letras (a-z, A-Z)
              </li>
              <li className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                Contener números (0-9)
              </li>
              <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                Tener al menos 8 caracteres
              </li>
            </ul>
          </div>
        )}

        {isGlobal && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Empresas
            </label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
              {companies.length === 0 ? (
                <p className="text-sm text-gray-400">No hay empresas disponibles</p>
              ) : (
                companies.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value={c.id}
                      checked={formData.companies.includes(c.id)}
                      onChange={(e) => {
                        const cid = parseInt(e.target.value, 10);
                        setFormData(prev => ({
                          ...prev,
                          companies: e.target.checked
                            ? [...prev.companies, cid]
                            : prev.companies.filter(x => x !== cid)
                        }));
                      }}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{c.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        <Select
          label="Rol"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value, plant_id: '' })}
          options={roleOptions}
          required
        />

        {formData.role === 'Jefe de Planta' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Planta Asignada
            </label>
            <select
              value={formData.plant_id}
              onChange={(e) => setFormData({ ...formData, plant_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Sin planta específica (todas) —</option>
              {plants.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            fullWidth
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Usuario'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserModal;
